import {
  dropInstance,
  getInstanceStatus,
  startInstance,
  stopInstance,
  upgradeInstance,
  getInstances,
  createInstance,
  getInstanceNodegroup,
  getInstanceHadoop,
  getInstanceMysql,
  getInstanceVIA,
  updateInstanceNodegroups,
  updateInstanceHadoop,
  updateInstanceMysql,
  updateInstanceVIA,
  updateInstanceDomain,
  retryCreateInstance,
  getInstanceDruid,
  getInstanceTemplate,
  disableInstanceDruid,
  enableInstanceDruid,
  updateInstanceDruid,
  getInstanceGitea,
  disableInstanceGitea,
  enableInstanceGitea,
  updateInstanceGitea,
  getInstanceNeo4j,
  disableInstanceNeo4j,
  enableInstanceNeo4j,
  updateInstanceNeo4j,
} from "@/apis";
// Single definition, kept in the converter module so there is exactly one JS copy of it.
import { GITEA_DEFAULT_PROFILE, NEO4J_DEFAULT_PROFILE } from "@/components/instance/config/ServerToClientConverter";

export { GITEA_DEFAULT_PROFILE, NEO4J_DEFAULT_PROFILE };

const handleInstanceResponse = (response) => {
  if (response?.status?.toLowerCase() === "error") {
    throw new Error(response.error || response.message);
  } else {
    return response.value;
  }
};

export const RESOURCE_TYPE_INSTANCES = "instances";
export const CHANGE_NODEGROUP = "changeNodeGroup";
export const CHANGE_HADOOP = "changeHadoop";
export const CHANGE_MYSQL = "changeMySQL";
export const CHANGE_VIA = "changeVIA";
export const CHANGE_DOMAIN_NAME = "changeDomainName";
export const CHANGE_DRUID = "changeDruid";
export const ENABLE_DRUID = "enableDruid";
export const DISABLE_DRUID = "disableDruid";
export const CHANGE_GITEA = "changeGitea";
export const ENABLE_GITEA = "enableGitea";
export const DISABLE_GITEA = "disableGitea";
export const CHANGE_NEO4J = "changeNeo4j";
export const ENABLE_NEO4J = "enableNeo4j";
export const DISABLE_NEO4J = "disableNeo4j";

export const InstancesDataProvider = {
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    const response = await getInstances();
    const data = response.value;
    return {
      data,
      total: data.length,
    };
  },

  getOne: async ({ resource, id, meta }) => {
    if (meta?.action === CHANGE_NODEGROUP) {
      const data = await getInstanceNodegroup(id);
      return handleInstanceResponse(data);
    } else if (meta?.action === CHANGE_HADOOP) {
      const data = await getInstanceHadoop(id);
      return handleInstanceResponse(data);
    } else if (meta?.action === CHANGE_MYSQL) {
      const data = await getInstanceMysql(id);
      return handleInstanceResponse(data);
    } else if (meta?.action === CHANGE_VIA) {
      const data = await getInstanceVIA(id);
      return handleInstanceResponse(data);
    } else if(meta?.action === CHANGE_DRUID) {
      const data = await getInstanceDruid(id);
      return handleInstanceResponse(data);
    } else if(meta?.action === ENABLE_DRUID) {
      let data = await getInstanceStatus(id);
      let template = "small-non-ha";
      if(data?.value?.full_profile?.template) {
        template = data?.value?.full_profile?.template;
      }
      data = await getInstanceTemplate(template);
      if(data?.value?.profile) {
        const { nodegroups, druid } = data.value.profile;
        return {nodegroups, druid};
      }
    } else if(meta?.action === CHANGE_GITEA) {
      const data = await getInstanceGitea(id);
      return handleInstanceResponse(data);
    } else if(meta?.action === ENABLE_GITEA) {
      let data = await getInstanceStatus(id);
      let template = "small-non-ha";
      if(data?.value?.full_profile?.template) {
        template = data?.value?.full_profile?.template;
      }
      data = await getInstanceTemplate(template);
      if(data?.value?.profile) {
        const { gitea } = data.value.profile;
        // A stored profile template predating gitea has no section for it, and the DB row wins
        // over the shipped profile_*.yaml -- so on an already-seeded deployment this is empty
        // until an admin re-saves each template. Returning it empty strands the form: with no
        // derived defaults, UpdateInstanceProfile never sets its ready flag and renders
        // "loading..." indefinitely. Mirror the server-side fallback in SetupGiteaStep.
        return {gitea: (gitea && gitea.length) ? gitea : [GITEA_DEFAULT_PROFILE]};
      }
    } else if(meta?.action === CHANGE_NEO4J) {
      const data = await getInstanceNeo4j(id);
      return handleInstanceResponse(data);
    } else if(meta?.action === ENABLE_NEO4J) {
      let data = await getInstanceStatus(id);
      let template = "small-non-ha";
      if(data?.value?.full_profile?.template) {
        template = data?.value?.full_profile?.template;
      }
      data = await getInstanceTemplate(template);
      if(data?.value?.profile) {
        const { neo4j } = data.value.profile;
        // Same reasoning as ENABLE_GITEA above: a stored profile template predating neo4j has
        // no section for it, so fall back to the canonical default (mirrors the server-side
        // fallback in TenantUtil.getNeo4j()).
        return {neo4j: (neo4j && neo4j.length) ? neo4j : [NEO4J_DEFAULT_PROFILE]};
      }
    }
    const data = await getInstanceStatus(id);
    // fake result
    //data.value.connectorUrls = {
    //  kubernetes: `https://gf0703yy4j.execute-api.us-east-1.amazonaws.com/v1/k8s`,
    //  logs: `https://gf0703yy4j.execute-api.us-east-1.amazonaws.com/v1/syslog`,
    //  snmptrap: `https://gf0703yy4j.execute-api.us-east-1.amazonaws.com/v1/snmptrap`
    //}
    return data.value;
  },
  create: async ({ resource, variables, meta }) => {
    const data = await createInstance(variables);
    return { data };
  },

  update: async ({ id, meta, variables }) => {
    // if (meta?.action === CHANGE_NODEGROUP) {
    //   const resp = await updateInstanceNodegroups(id, variables);
    //   return handleInstanceResponse(resp);
    // } else if (meta?.action === CHANGE_HADOOP) {
    //   const resp = await updateInstanceHadoop(id, variables);
    //   return handleInstanceResponse(resp);
    // } else if (meta?.action === CHANGE_MYSQL) {
    //   const resp = await updateInstanceMysql(id, variables);
    //   return handleInstanceResponse(resp);
    // } else if (meta?.action === CHANGE_VIA) {
    //   const resp = await updateInstanceVIA(id, variables);
    //   return handleInstanceResponse(resp);
    // } else if (meta?.action === CHANGE_DOMAIN_NAME) {
    //   const resp = await updateInstanceDomain(id, variables);
    //   return handleInstanceResponse(resp);
    // }

    const { action, data } = variables;
    if (action === "stop") {
      const resp = await stopInstance(id);
      return handleInstanceResponse(resp);
    } else if (action === "start") {
      const resp = await startInstance(id);
      return handleInstanceResponse(resp);
    } else if (action === "upgrade") {
      const resp = await upgradeInstance(id, data);
      return handleInstanceResponse(resp);
    } else if (action === "retry") {
      const resp = await retryCreateInstance(id);
      return handleInstanceResponse(resp);
    } else if (action === CHANGE_NODEGROUP) {
      const resp = await updateInstanceNodegroups(id, data);
      return handleInstanceResponse(resp);
    } else if (action === CHANGE_HADOOP) {
      const resp = await updateInstanceHadoop(id, data);
      return handleInstanceResponse(resp);
    } else if (action === CHANGE_MYSQL) {
      const resp = await updateInstanceMysql(id, data);
      return handleInstanceResponse(resp);
    } else if (action === CHANGE_VIA) {
      const resp = await updateInstanceVIA(id, data);
      return handleInstanceResponse(resp);
    } else if (action === CHANGE_DOMAIN_NAME) {
      const resp = await updateInstanceDomain(id, data);
      return handleInstanceResponse(resp);
    } else if (action === CHANGE_DRUID) {
      const resp = await updateInstanceDruid(id, data);
      return handleInstanceResponse(resp);
    } else if (action === ENABLE_DRUID) {
      const resp = await enableInstanceDruid(id, data);
      return handleInstanceResponse(resp);
    } else if (action === DISABLE_DRUID) {
      const resp = await disableInstanceDruid(id);
      return handleInstanceResponse(resp);
    } else if (action === CHANGE_GITEA) {
      const resp = await updateInstanceGitea(id, data);
      return handleInstanceResponse(resp);
    } else if (action === ENABLE_GITEA) {
      const resp = await enableInstanceGitea(id, data);
      return handleInstanceResponse(resp);
    } else if (action === DISABLE_GITEA) {
      const resp = await disableInstanceGitea(id);
      return handleInstanceResponse(resp);
    } else if (action === CHANGE_NEO4J) {
      const resp = await updateInstanceNeo4j(id, data);
      return handleInstanceResponse(resp);
    } else if (action === ENABLE_NEO4J) {
      const resp = await enableInstanceNeo4j(id, data);
      return handleInstanceResponse(resp);
    } else if (action === DISABLE_NEO4J) {
      const resp = await disableInstanceNeo4j(id);
      return handleInstanceResponse(resp);
    }
  },
  deleteOne: async ({ resource, id }) => {
    const resp = await dropInstance(id);
    return resp.value;
  },
  getApiUrl: async () => {
    throw new Error("Function not implemented.");
  },
};
